/*
* === ReportsPage.js (With Executive KPIs & EFT Export Features) ===
*/
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Container, Typography, Card, CardHeader, CardContent, Box, Alert, Button, Grid, Paper, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, Accordion, AccordionSummary, AccordionDetails, DialogContentText
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import {
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import SpeedIcon from '@mui/icons-material/Speed';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import autoTable from 'jspdf-autotable';

const COLORS = {
  efficiency: ['#6a11cb', '#2575fc', '#ff6b6b', '#4ecdc4', '#45b7d1', '#ffa502', '#ff6348'],
  impact: ['#ff9ff3', '#feca57', '#48dbfb', '#1dd1a1', '#ff3838'],
  financials: ['#ffa502', '#ff6348', '#32ff7e', '#1e90ff'],
  roi: ['#1e90ff', '#32ff7e', '#ff3838'],
  audit: ['#3742fa', '#2f3542', '#57606f']
};

const allReports = [
    { id: 'A', title: 'A. Conversion Rate Funnel', endpoint: 'conversion-funnel', type: 'bar', group: 'Efficiency & Volume', colorSet: 'efficiency', dataKey: 'count', labelKey: 'Status' },
    { id: 'C', title: 'C. Success Rate by Fund', endpoint: 'success-rate-by-fund', type: 'pie', group: 'Efficiency & Volume', colorSet: 'efficiency', dataKey: 'count', labelKey: 'Status' },
    { id: 'B', title: 'B. Processing Time Metrics (Days)', endpoint: 'processing-time', type: 'metrics', group: 'Efficiency & Volume', colorSet: 'efficiency' },
    { id: 'D', title: 'D. Accepted by Province (Alignment)', endpoint: 'alignment-location', type: 'bar', group: 'Impact & Demographics', colorSet: 'impact', dataKey: 'AcceptedCount', labelKey: 'Province' },
    { id: 'E', title: 'E. Success Rate by Gender/Race', endpoint: 'success-rate-demographics', type: 'table', group: 'Impact & Demographics', colorSet: 'impact' },
    { id: 'F', title: 'F. Funding by Applicant Need', endpoint: 'funding-by-need', type: 'pie', group: 'Financials & Risk', colorSet: 'financials', dataKey: 'TotalDisbursed', labelKey: 'Category' },
    { id: 'H', title: 'H. Cohort Cost Analysis', endpoint: 'cohort-cost', type: 'metrics', group: 'Financials & Risk', colorSet: 'financials' },
    { id: 'G', title: 'G. Document Risk (Funded Apps with Issues)', endpoint: 'document-risk', type: 'table', group: 'Financials & Risk', colorSet: 'financials' },
    { id: 'I', title: 'I. Academic Performance (Avg GPA)', endpoint: 'academic-performance', type: 'bar', group: 'ROI & Retention', colorSet: 'roi', dataKey: 'AvgGPA', labelKey: 'TermName' },
    { id: 'J', title: 'J. Student Retention Rate', endpoint: 'retention-rate', type: 'retention', group: 'ROI & Retention', colorSet: 'roi' },
    { id: 'K', title: 'Workflow Audit Log', endpoint: 'workflow-audit', type: 'table', group: 'Audit Log', colorSet: 'audit' }
];

const ChartCard = ({ title, data, type, endpoint, colorSet, onEmail, onChartClick, dataKey, labelKey }) => {
    const chartRef = useRef();
    const colors = COLORS[colorSet] || COLORS.efficiency;
    const supportsDrilldown = ['conversion-funnel', 'success-rate-by-fund', 'alignment-location'].includes(endpoint);
    const isClickable = onChartClick && supportsDrilldown;
    const [loadingAction, setLoadingAction] = useState('');

    const exportPDF = async () => {
        if (!data || (Array.isArray(data) && data.length === 0)) return alert("No data available to export.");
        setLoadingAction('pdf');
        try {
            await new Promise(resolve => setTimeout(resolve, 100));
            const doc = new jsPDF();
            doc.text("BrightSparc Report", 14, 16);
            doc.setFontSize(12);
            doc.text(title, 14, 24);
            doc.setFontSize(10);
            doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);

            if (chartRef.current && type !== 'table') {
                const canvas = await html2canvas(chartRef.current, { scale: 2, backgroundColor: '#ffffff', logging: false });
                const imgData = canvas.toDataURL('image/png');
                const imgWidth = 180; 
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                doc.addImage(imgData, 'PNG', 14, 40, imgWidth, imgHeight);
            } else if (type === 'table' && Array.isArray(data) && data.length > 0) {
                const headers = Object.keys(data[0]);
                const rows = data.map(row => headers.map(h => typeof row[h] === 'object' ? JSON.stringify(row[h]) : row[h]));
                autoTable(doc, { head: [headers], body: rows, startY: 40 });
            }
            doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
        } catch (e) { 
            alert("Failed to generate PDF."); 
        } finally { setLoadingAction(''); }
    };

    const exportCSV = async () => { 
        if (!data || (Array.isArray(data) && data.length === 0)) return alert("No data available to export.");
        let exportData = data;
        if (type === 'metrics' && !Array.isArray(data)) exportData = [data]; 
        const csv = Papa.unparse(exportData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, `${title.replace(/\s+/g, '_')}.csv`);
    };

    const renderChart = () => {
        if (!data || (Array.isArray(data) && data.length === 0)) return <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No data available</Typography>;

        if (type === 'metrics') {
             const metricsData = Array.isArray(data) ? data[0] : data;
             if (!metricsData) return <Typography>No data</Typography>;
             const metrics = Object.entries(metricsData).filter(([k]) => k !== 'id');
             return (
                <Grid container spacing={2} sx={{ pt: 2 }} ref={chartRef}>
                    {metrics.map(([key, value]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                            <Paper elevation={4} sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block', mb: 0.5 }}>{key.replace(/([A-Z])/g, ' $1').trim()}</Typography>
                                <Typography variant="h6" fontWeight="bold" color="primary">{typeof value === 'number' ? value.toFixed(1) : (value || '0')}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
             );
        }

        if (type === 'bar') {
            return (
                <div ref={chartRef}>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={data} onClick={(e) => isClickable && e && e.activeLabel && onChartClick(endpoint, e.activeLabel)} style={{ cursor: isClickable ? 'pointer' : 'default' }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey={labelKey || 'name'} />
                            <YAxis />
                            <RechartsTooltip />
                            <Bar dataKey={dataKey || 'count'} fill={colors[0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            );
        }
        if (type === 'pie') {
            return (
                <div ref={chartRef}>
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart style={{ cursor: isClickable ? 'pointer' : 'default' }}>
                            <Pie 
                              data={data} 
                              dataKey={dataKey || 'count'} 
                              nameKey={labelKey || 'name'} 
                              cx="50%" 
                              cy="50%" 
                              innerRadius={60} 
                              outerRadius={80} 
                              label
                              onClick={(entry) => isClickable && entry && onChartClick(endpoint, entry[labelKey || 'name'])}
                            >
                                {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                            </Pie>
                            <RechartsTooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            );
        }
        
        if (type === 'table') {
             const headers = Object.keys(data[0] || {});
             return (
                 <Box sx={{overflowX: 'auto'}}>
                    <table style={{width: '100%', borderCollapse: 'collapse'}}>
                        <thead>
                            <tr>{headers.map(h => <th key={h} style={{textAlign:'left', padding: 8, borderBottom: '1px solid #ddd'}}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                            {data.map((row, i) => (
                                <tr key={i}>
                                    {headers.map(h => (
                                        <td key={h} style={{padding: 8, borderBottom: '1px solid #eee'}}>
                                            {typeof row[h] === 'object' ? JSON.stringify(row[h]) : row[h]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                 </Box>
             );
        }
        
        if (type === 'retention') {
             return (
                 <div ref={chartRef}>
                     <ResponsiveContainer width="100%" height={280}>
                         <BarChart data={data} layout="vertical" margin={{ left: 100 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="FundName" type="category" width={150} tick={{fontSize: 10}} />
                            <RechartsTooltip />
                            <Bar dataKey="TotalFunded" fill="#8884d8" name="Total Students" />
                            <Bar dataKey="VerifiedRetentionCount" fill="#82ca9d" name="Retained (Verified)" />
                         </BarChart>
                     </ResponsiveContainer>
                 </div>
             );
        }

        return <Typography>Chart type not supported</Typography>;
    };

    return (
        <Grid item xs={12} md={type === 'table' || type === 'retention' ? 12 : 6}>
            <Card elevation={8} sx={{ borderRadius: 3 }}>
                <CardHeader title={title} subheader={isClickable ? "Click chart segment to view detailed applicants" : ""} />
                <CardContent>{renderChart()}</CardContent>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button size="small" startIcon={<DownloadIcon />} onClick={exportCSV} disabled={!!loadingAction}>{loadingAction === 'csv' ? '...' : 'CSV'}</Button>
                    <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={exportPDF} disabled={!!loadingAction}>{loadingAction === 'pdf' ? '...' : 'PDF'}</Button>
                    <Button size="small" startIcon={<EmailIcon />} onClick={() => onEmail(endpoint, title, data)}>Email</Button>
                </Box>
            </Card>
        </Grid>
    );
};

const ReportsPage = () => {
  const { token, user } = useAuth();
  const [reportData, setReportData] = useState({});
  const [kpiData, setKpiData] = useState({ TotalDisbursed: 0, ActiveScholars: 0, AvgDaysToProcess: 0, MissingDocRisks: 0 });
  const [loading, setLoading] = useState(true);
  const [bursaryList, setBursaryList] = useState([]);
  const [filters, setFilters] = useState({ fundId: '', startDate: '', endDate: '' });
  const [alerts, setAlerts] = useState([]);
  
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailConfig, setEmailConfig] = useState({ recipient: '', subject: '', body: '', reportKey: '', dataPayload: null });
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState({ open: false, title: '', message: '', severity: 'info' });
  const [drilldown, setDrilldown] = useState({ open: false, title: '', data: [], loading: false });

  const scopeLabel = useMemo(() => {
      if (user?.role === 'SuperUser') return 'Scoped to: All Companies (Platform-wide)';
      return `Scoped to: ${user?.companyName || user?.CompanyName || 'Your Organization'}`;
  }, [user]);

  const visibleReports = useMemo(() => {
      if (user?.role === 'SuperUser') return allReports;
      return allReports.filter(r => r.endpoint !== 'workflow-audit');
  }, [user?.role]);

  const groupedReports = useMemo(() => {
      return visibleReports.reduce((acc, report) => {
          (acc[report.group] = acc[report.group] || []).push(report);
          return acc;
      }, {});
  }, [visibleReports]);

  useEffect(() => {
    api.get('/admin/reports/bursary-list', { headers: { Authorization: `Bearer ${token}` } })
       .then(res => setBursaryList(res.data))
       .catch(err => console.error("Failed to fetch bursaries", err));
  }, [token]);

  const fetchAll = useCallback(async () => {
      setLoading(true);
      try {
          const [reportsRes, kpiRes] = await Promise.all([
              api.get('/admin/reports/all', { headers: { Authorization: `Bearer ${token}` }, params: filters }),
              api.get('/admin/reports/kpis', { headers: { Authorization: `Bearer ${token}` }, params: filters })
          ]);
          setReportData(reportsRes.data);
          setKpiData(kpiRes.data || {});
      } catch (e) { 
          setAlerts([{type: 'error', message: 'Failed to load reports.'}]); 
      } finally { 
          setLoading(false); 
      }
  }, [token, filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleEftExport = async () => {
      try {
          const res = await api.get('/admin/reports/eft-export', {
              headers: { Authorization: `Bearer ${token}` },
              params: filters
          });
          if (!res.data || res.data.length === 0) {
              return setFeedbackModal({ open: true, title: 'No Payments', message: 'No funded applicants with bank details found for export.', severity: 'info' });
          }
          const csv = Papa.unparse(res.data);
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          saveAs(blob, `EFT_Payment_Batch_${new Date().toISOString().split('T')[0]}.csv`);
      } catch (e) {
          setFeedbackModal({ open: true, title: 'Export Failed', message: 'Failed to generate EFT payment file.', severity: 'error' });
      }
  };

  const handleChartClick = async (endpoint, filterValue) => {
      if (!filterValue) return;
      setDrilldown({ open: true, title: `Details for: ${filterValue}`, data: [], loading: true });
      try {
          const res = await api.get('/admin/reports/drilldown', {
              headers: { Authorization: `Bearer ${token}` },
              params: { ...filters, reportKey: endpoint, filterValue }
          });
          setDrilldown(prev => ({ ...prev, data: res.data, loading: false }));
      } catch (e) {
          setDrilldown(prev => ({ ...prev, loading: false }));
          setFeedbackModal({ open: true, title: 'Drilldown Error', message: 'Failed to fetch detailed data.', severity: 'error' });
      }
  };

  const handleEmail = async () => {
      if (!emailConfig.recipient) return setFeedbackModal({ open: true, title: 'Validation Error', message: 'Please enter a recipient email.', severity: 'warning' });
      setIsEmailSending(true); 
      try {
        const logsToSend = Array.isArray(emailConfig.dataPayload) ? emailConfig.dataPayload : [emailConfig.dataPayload];
        await api.post('/admin/reports/email', {
            recipient: emailConfig.recipient,
            subject: emailConfig.subject,
            body: emailConfig.body,
            reportKey: emailConfig.reportKey,
            filters,
            logs: logsToSend
        }, { headers: { Authorization: `Bearer ${token}` } });
        setEmailOpen(false);
        setFeedbackModal({ open: true, title: 'Success', message: 'Email Report sent successfully!', severity: 'success' });
      } catch(e) {
          setFeedbackModal({ open: true, title: 'Error', message: e.response?.data?.message || 'Failed to send email.', severity: 'error' });
      } finally { setIsEmailSending(false); }
  };
  
  const openEmailModal = (key, title, data) => { 
      setEmailConfig({ ...emailConfig, reportKey: key, subject: title + ' Report', dataPayload: data });
      setEmailOpen(true); 
  };

  const closeFeedbackModal = () => setFeedbackModal(prev => ({ ...prev, open: false }));

  const drilldownColumns = [
      { field: 'Id', headerName: 'App ID', width: 90 },
      { field: 'FullName', headerName: 'Applicant Name', flex: 1 },
      { field: 'Email', headerName: 'Email', flex: 1 },
      { field: 'Status', headerName: 'Status', width: 130 },
      { field: 'SubmittedAt', headerName: 'Submitted', width: 150, valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'N/A' }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, pb: 8 }}>
        <Typography variant="h4" gutterBottom>
            Reports Dashboard
            <Chip label={scopeLabel} color={user?.role === 'SuperUser' ? 'secondary' : 'primary'} variant="outlined" size="small" sx={{ ml: 2, verticalAlign: 'middle', fontWeight: 'bold' }} />
        </Typography>
        
        {/* Filter Toolbar */}
        <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
             <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                 <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Bursary Fund</InputLabel>
                    <Select value={filters.fundId} label="Bursary Fund" onChange={(e) => setFilters({...filters, fundId: e.target.value})}>
                        <MenuItem value="">All Funds</MenuItem>
                        {bursaryList.map(b => <MenuItem key={b.Id} value={b.Id}>{b.FundName}</MenuItem>)}
                    </Select>
                 </FormControl>
                 <TextField type="date" label="Start Date" InputLabelProps={{shrink:true}} size="small" value={filters.startDate} onChange={(e)=>setFilters({...filters, startDate:e.target.value})} />
                 <TextField type="date" label="End Date" InputLabelProps={{shrink:true}} size="small" value={filters.endDate} onChange={(e)=>setFilters({...filters, endDate:e.target.value})} />
                 <Button variant="contained" onClick={fetchAll}>Filter</Button>
             </Box>
             
             {/* 🚨 FEATURE 2: EFT Ready Payment Export Button */}
             <Button 
                variant="outlined" 
                color="success" 
                startIcon={<AccountBalanceIcon />} 
                onClick={handleEftExport}
                sx={{ fontWeight: 'bold' }}
             >
                 Export Bank Payment File (EFT)
             </Button>
        </Paper>

        {/* 🚨 FEATURE 1: Executive KPI Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={3} sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'white', borderRadius: 2 }}>
                    <AccountBalanceIcon fontSize="large" />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>Total Disbursed</Typography>
                    <Typography variant="h5" fontWeight="bold">R {Number(kpiData.TotalDisbursed || 0).toLocaleString()}</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={3} sx={{ p: 2, textAlign: 'center', bgcolor: 'success.main', color: 'white', borderRadius: 2 }}>
                    <SchoolIcon fontSize="large" />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>Active Funded Scholars</Typography>
                    <Typography variant="h5" fontWeight="bold">{kpiData.ActiveScholars || 0}</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={3} sx={{ p: 2, textAlign: 'center', bgcolor: 'info.main', color: 'white', borderRadius: 2 }}>
                    <SpeedIcon fontSize="large" />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>Avg Processing Time</Typography>
                    <Typography variant="h5" fontWeight="bold">{kpiData.AvgDaysToProcess || 0} Days</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={3} sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.main', color: 'white', borderRadius: 2 }}>
                    <WarningAmberIcon fontSize="large" />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>Missing Document Risks</Typography>
                    <Typography variant="h5" fontWeight="bold">{kpiData.MissingDocRisks || 0}</Typography>
                </Paper>
            </Grid>
        </Grid>

        {alerts.map((a, i) => <Alert key={i} severity={a.type} sx={{ mb: 2 }}>{a.message}</Alert>)}

        {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : 
            Object.entries(groupedReports).map(([groupName, reports]) => (
                <Accordion key={groupName} defaultExpanded sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">{groupName}</Typography></AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={3}>
                            {reports.map(r => (
                                <ChartCard 
                                    key={r.id} 
                                    title={r.title} 
                                    data={reportData[r.endpoint]} 
                                    type={r.type} 
                                    endpoint={r.endpoint}
                                    colorSet={r.colorSet}
                                    dataKey={r.dataKey} 
                                    labelKey={r.labelKey}
                                    onEmail={openEmailModal}
                                    onChartClick={handleChartClick}
                                />
                            ))}
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            ))
        }
        
        {/* Interactive Drill-down Modal */}
        <Dialog open={drilldown.open} onClose={() => setDrilldown({...drilldown, open: false})} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {drilldown.title}
                <IconButton onClick={() => setDrilldown({...drilldown, open: false})}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ height: 400 }}>
                {drilldown.loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
                ) : (
                    <DataGrid 
                        rows={drilldown.data} 
                        columns={drilldownColumns} 
                        getRowId={(row) => row.Id}
                        pageSizeOptions={[5, 10, 25]}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                        disableRowSelectionOnClick
                    />
                )}
            </DialogContent>
        </Dialog>

        {/* Email & Feedback Modals */}
        <Dialog open={emailOpen} onClose={() => setEmailOpen(false)}>
             <DialogTitle>Email Report</DialogTitle>
             <DialogContent>
                 <TextField label="Recipient" fullWidth margin="dense" value={emailConfig.recipient} onChange={e => setEmailConfig({...emailConfig, recipient: e.target.value})} />
                 <TextField label="Subject" fullWidth margin="dense" value={emailConfig.subject} onChange={e => setEmailConfig({...emailConfig, subject: e.target.value})} />
                 <TextField label="Body" multiline rows={3} fullWidth margin="dense" value={emailConfig.body} onChange={e => setEmailConfig({...emailConfig, body: e.target.value})} />
             </DialogContent>
             <DialogActions>
                 <Button onClick={() => setEmailOpen(false)} disabled={isEmailSending}>Cancel</Button>
                 <Button onClick={handleEmail} variant="contained" disabled={isEmailSending}>
                     {isEmailSending ? <CircularProgress size={24} color="inherit" /> : "Send"}
                 </Button>
             </DialogActions>
        </Dialog>

        <Dialog open={feedbackModal.open} onClose={closeFeedbackModal}>
            <DialogTitle sx={{ color: feedbackModal.severity === 'error' ? 'error.main' : feedbackModal.severity === 'success' ? 'success.main' : 'primary.main' }}>
                {feedbackModal.title}
            </DialogTitle>
            <DialogContent><DialogContentText>{feedbackModal.message}</DialogContentText></DialogContent>
            <DialogActions><Button onClick={closeFeedbackModal} color="primary" autoFocus>Close</Button></DialogActions>
        </Dialog>

    </Container>
  );
};

export default ReportsPage;
